import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import microsoftlogo from 'assets/microsoftlogo.png';
import BaseModal from 'components/Modals/BaseModal';
import { GetPermListForRole } from 'data/permData';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ms_getSecurityGroups, tryMsAuthSilent } from 'services/auth/ms.service';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { ms_getRoleLinks, ms_updateRoleLinks } from 'services/roles/ms.service';
import { createRole, getAllRoles } from 'services/roles/roles.service';
import ImportMSRoles from './ImportMSRoles';
import RolesTable from './RolesTable';

export default function RolesTab({ list, perms, setRoleList }) {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();

  const [showConfirmLinkRoles, setShowConfirmLinkRoles] = useState(false);
  const [newRoles, setNewRoles] = useState([]);
  const [showImportRoles, setShowImportRoles] = useState(false);
  const [isMsalAuthenticated, setIsMsalAuthenticated] = useState(false);
  const [linkMulti, setLinkMulti] = useState({});
  const [msGroups, setMsGroups] = useState([]);

  const onCreateRole = async () => {
    navigate('/system/createrole');
  };

  useEffect(() => {
    const fetchMSal = async () => {
      try {
        const msalauth = await tryMsAuthSilent(instance);

        setIsMsalAuthenticated(!!msalauth);
      } catch (error) {
        console.error('Error fetching msalAuth:', error);
        setIsMsalAuthenticated(false);
      }
    };
    fetchMSal();
  }, [instance]);

  const getMsInfo = async () => {
    //MS Graph API
    const scopes = ['Group.Read.All'];
    try {
      const msRes = await tryMsAuthSilent(instance, scopes);
      if (!msRes) {
        alert('Microsoft authentication not configured or no account is signed in.');
        return;
      }

      const client = Client.init({
        authProvider: (done) => {
          done(null, msRes.accessToken);
        },
      });

      let groups = await ms_getSecurityGroups(instance);

      setMsGroups(groups.data.result || []);

      // get links
      const res = await ms_getRoleLinks(instance);
      const links = res.data.result || {};

      const linkMultis = {};
      Object.keys(links).forEach((link) => {
        linkMultis[link] = links[link];
      });

      setLinkMulti(linkMultis);
    } catch (err) {
      console.error('getMsInfo failed', err);
      alert('Failed to fetch Microsoft groups. Check console for details.');
    }
  };

  const onImportRoles = async () => {
    setShowImportRoles(true);
    getMsInfo();
  };

  const sendImportRoles = async () => {
    let linkObj = { ...linkMulti };

    if (newRoles.length > 0) {
      let permData;

      const deviceTypeArr = Object.keys(deviceTypes).flatMap((systemType) => {
        return Object.keys(deviceTypes[systemType]).flatMap((deviceType) => {
          if (
            Object.keys(deviceTypes[systemType][deviceType]).length > 0 &&
            !Object.keys(deviceTypes[systemType][deviceType]).includes('validDevices')
          )
            return Object.keys(deviceTypes[systemType][deviceType]).map(
              (subType) => systemType + ' ' + deviceType + '_' + subType,
            );
          else return systemType + ' ' + deviceType;
        });
      });

      permData = GetPermListForRole(deviceTypeArr)
        .map((perm) => {
          return {
            [perm.name.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_')]: false,
          };
        })
        .reduce((acc, current) => {
          return { ...acc, ...current };
        });

      //create new roles
      for (const newRole of newRoles) {
        const newRoleId = await createRole(newRole.name, [], JSON.stringify(permData), '[]');

        linkObj = { ...linkObj, [newRole.id]: [newRoleId.data.result] };
      }
    }

    //NOTE: If someone calls this and Microsoft does not give up ALL of
    //      the MsGroup IDs, then this will break and unlink things that
    //      are valid.
    let finalLinks = {};
    Object.keys(linkObj).forEach((group) => {
      const match = msGroups.find((find) => find.id === group);
      if (match)
        finalLinks = {
          ...finalLinks,
          [group]: {
            roles: linkObj[group],
            name: match.displayName,
          },
        };
    });

    let res;
    try {
      res = await ms_updateRoleLinks(instance, finalLinks);
    } catch (err) {
      console.error('ms_updateRoleLinks failed', err);
      alert('Failed to sync role links with Microsoft. Check console for details.');
      return;
    }

    let newUsers = res.data.syncResults.filter((r) => r.type === 'newUser');

    let affectedUsers = res.data.syncResults.filter((r) => r.status !== 'error');
    if (affectedUsers) {
      let failedUsers = [];
      let successUsers = [];
      let failedMessage = '';
      let successMessage = '';

      newUsers.forEach((user) => {
        if (user.status === 'error') failedUsers.push(user.user.email);
        else successUsers.push(user.user.email);
      });
      if (successUsers.length > 0) {
        successMessage = `Successfully imported ${successUsers.length} users.
      `;
        // \n${successUsers.join("\n")}
      }
      if (failedUsers.length > 0) {
        failedMessage = `Failed to import ${failedUsers.length} users:\n${failedUsers.join('\n')}`;
      }

      // let affectedMessage = ""
      // if (affectedUsers.length > 0) affectedMessage = `Modified ${affectedUsers.length} users.\n\n`

      let finalMessage = '';
      if (successMessage && failedMessage) finalMessage = successMessage + '\n' + failedMessage;
      else if (successMessage) finalMessage = successMessage;
      else if (failedMessage) finalMessage = failedMessage;

      if (finalMessage) alert(finalMessage);
      // else if (affectedMessage) alert(affectedMessage);
      else alert('Sync updated.');
    }
    setShowImportRoles(false);
    setShowConfirmLinkRoles(false);

    //get roles
    const res2 = await getAllRoles(instance);
    setRoleList([...res2.data.result]);
    setNewRoles([]);
  };

  return (
    <div className="w-100 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <div className="d-flex w-100 justify-content-between">
        <div className="d-flex align-self-end bg-primary rounded-3">
          <div
            style={{
              height: 39,
              borderRadius: 10,
            }}
          ></div>
        </div>
      </div>

      <RolesTable list={list} perms={perms} setRoleList={setRoleList} msalInstance={instance} />
      <div className="d-flex justify-content-between mt-3">
        {perms.testPermission([8, 11, 12, 13]) && isMsalAuthenticated && (
          <Button
            className="d-flex align-items-center justify-content-center p-1 bg-primary rounded"
            style={{
              height: 65,
            }}
            onClick={onImportRoles}
          >
            <img src={microsoftlogo} style={{ height: 20 }} />
            <div className="ms-2">Link Security Groups</div>
          </Button>
        )}
        {showImportRoles && (
          <BaseModal
            show={showImportRoles}
            handleShow={() => setShowImportRoles(false)}
            title={
              <div className="flex-row gap-2">
                <img src={microsoftlogo} style={{ height: 30 }} />
                <div>Link Security Groups </div>
              </div>
            }
          >
            <ImportMSRoles
              list={list}
              msGroups={msGroups}
              linkMulti={linkMulti}
              setLinkMulti={setLinkMulti}
              showConfirmLinkRoles={showConfirmLinkRoles}
              setShowConfirmLinkRoles={setShowConfirmLinkRoles}
              newRoles={newRoles}
              setNewRoles={setNewRoles}
              sendImportRoles={sendImportRoles}
            />
          </BaseModal>
        )}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['CreateRole']) && (
          <Button
            className="d-flex align-items-center justify-content-center bg-primary rounded"
            style={{
              height: 65,
            }}
            onClick={onCreateRole}
          >
            <div>Create Role</div>
          </Button>
        )}
      </div>
    </div>
  );
}
